// Piezas comunes del asistente del Plan de Emergencias v2 (PE-A3b): semáforo accesible (color, texto e
// ícono), traza, modal de confirmación propio (nunca alert/confirm del navegador) y campos de formulario.

import React, { useEffect, useRef, useState } from 'react';
import { etiquetaVista, vistaDePaso } from '../../lib/pe/pe-capitulos';
import { SEMAFORO } from '../../lib/pe/pe-resultados';
import type { EstadoTraza, Traza } from '../../lib/pe/pe-tipos';
import { formatoEc } from '../../lib/pe/pe-unidades';

export function Campo({ etiqueta, children, nota }: { etiqueta: string; children: React.ReactNode; nota?: React.ReactNode }) {
  return <label className="pe2-campo"><span>{etiqueta}</span>{children}{nota && <small>{nota}</small>}</label>;
}

export function Numero({ valor, onChange, unidad, disabled, entero, etiqueta }: {
  valor: string; onChange: (v: string) => void; unidad: string; disabled?: boolean; entero?: boolean; etiqueta?: string;
}) {
  return (
    <div className="pe2-unidad">
      <input type="text" inputMode={entero ? 'numeric' : 'decimal'} value={valor} disabled={disabled} aria-label={etiqueta}
        onChange={e => onChange(e.target.value)} />
      <em>{unidad}</em>
    </div>
  );
}

export function Semaforo({ estado }: { estado: EstadoTraza }) {
  const s = SEMAFORO[estado];
  return (
    <span className="pe2-semaforo" style={{ color: s.color, background: s.fondo, borderColor: s.color }}>
      <span aria-hidden="true" className="pe2-semaforo-icono">{s.icono}</span>{s.texto}
    </span>
  );
}

const valorTexto = (v: number | string | null, unidad: string) =>
  v === null ? '—' : `${typeof v === 'number' ? formatoEc(v, 2) : v}${unidad && unidad !== '—' ? ` ${unidad}` : ''}`;

// PE-C1: el paso viejo de la traza se muestra como el capítulo donde se completa ("destino" de la pantalla o, sin él,
// el mismo cálculo de pe-capitulos).
const destinoPorDefecto = (p: number, clave?: string) => { const d = vistaDePaso(p, clave); return etiquetaVista(d.vista, d.sub); };
export function TrazaItem({ t, irAPaso, origen, claveEvaluacion, destino = destinoPorDefecto }: {
  t: Traza; irAPaso?: (p: number, claveEvaluacion?: string) => void; origen?: string; claveEvaluacion?: string;
  destino?: (p: number, claveEvaluacion?: string) => string;
}) {
  return (
    <div className={`pe2-traza pe2-traza-${t.estado}`}>
      <div className="pe2-traza-cabeza">
        <Semaforo estado={t.estado} />
        <b>{origen ? `${origen} · ` : ''}{t.articulo_o_tabla}</b>
      </div>
      <div className="pe2-traza-cuerpo">
        <span>{t.norma}</span>
        {(t.estado === 'no_cumple' || t.estado === 'cumple') && (
          <span>Valor: {valorTexto(t.valor, t.unidad)}{t.umbral !== null ? ` · Umbral: ${typeof t.umbral === 'number' ? formatoEc(t.umbral, 2) : t.umbral}` : ''}</span>
        )}
        {t.estado === 'informativo' && t.valor !== null && <span>Valor: {valorTexto(t.valor, t.unidad)}</span>}
        {t.nota && <span>{t.nota}</span>}
        {t.estado === 'por_validar' && t.paso && (
          <span>
            {`Se completa en ${destino(t.paso, claveEvaluacion)}.`}{' '}
            {irAPaso && <button type="button" className="pe2-enlace" onClick={() => irAPaso(t.paso as number, claveEvaluacion)}>
              {`Ir a ${destino(t.paso, claveEvaluacion)}`}</button>}
          </span>
        )}
        <small className="pe2-fuente-doc">Fuente: {t.fuente_doc}</small>
      </div>
    </div>
  );
}

// Modal de confirmación propio, accesible (role="dialog", foco en "Cancelar", Escape cierra). Con "escribir",
// el botón de confirmar solo se habilita cuando se escribe ese texto exacto (borrar el plan, PE-A3c). Con "bloqueado"
// (PE-A4b: plan con versiones aprobadas), el aviso se muestra desde el inicio, no se pide escribir y no se confirma.
// F3a2: "children" agrega contenido (por ejemplo, una pregunta sí o no) y "listo" en false deshabilita el confirmar.
export function ModalConfirmar({ abierto, titulo, mensaje, textoConfirmar = 'Borrar', onConfirmar, onCancelar, ocupado, escribir, bloqueado, children, listo = true }: {
  abierto: boolean; titulo: string; mensaje: string; textoConfirmar?: string; onConfirmar: () => void; onCancelar: () => void; ocupado?: boolean;
  escribir?: string; bloqueado?: string; children?: React.ReactNode; listo?: boolean;
}) {
  const cancelar = useRef<HTMLButtonElement>(null);
  const [texto, setTexto] = useState('');
  useEffect(() => {
    if (!abierto) return;
    setTexto('');
    cancelar.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancelar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [abierto, onCancelar]);
  if (!abierto) return null;
  const falta = !!bloqueado || !listo || (!!escribir && texto.trim() !== escribir);
  return (
    <div className="pe2-modal-fondo">
      <div className="pe2-modal" role="dialog" aria-modal="true" aria-labelledby="pe2-modal-titulo">
        <h3 id="pe2-modal-titulo">{titulo}</h3>
        {bloqueado && <div className="pe2-aviso" role="status">{bloqueado}</div>}
        <p>{mensaje}</p>
        {children}
        {escribir && !bloqueado && (
          <label className="pe2-campo">
            <span>Para confirmar, escribe: <b>{escribir}</b></span>
            <input type="text" value={texto} onChange={e => setTexto(e.target.value)} disabled={ocupado} autoComplete="off" />
          </label>
        )}
        <div className="pe2-acciones">
          <button ref={cancelar} type="button" className="pe2-btn" onClick={onCancelar} disabled={ocupado}>Cancelar</button>
          <button type="button" className="pe2-btn peligro" onClick={onConfirmar} disabled={ocupado || falta}>{ocupado ? 'Borrando…' : textoConfirmar}</button>
        </div>
      </div>
    </div>
  );
}

// Número escrito con coma → número o null (vacío). Texto inválido → null (la pantalla lo marca aparte).
export const leerNum = (t: string): number | null => {
  const s = t.trim().replace(',', '.');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};
export const aTexto = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n).replace('.', ','));

// Campo numérico ligado a un number | null: conserva el texto mientras se escribe ("12," no se pierde) y
// solo se resincroniza si el valor cambia desde fuera.
export function NumeroValor({ valor, onChange, unidad, disabled, entero, etiqueta }: {
  valor: number | null | undefined; onChange: (n: number | null) => void; unidad: string; disabled?: boolean; entero?: boolean; etiqueta?: string;
}) {
  const [texto, setTexto] = React.useState(aTexto(valor));
  React.useEffect(() => {
    if (leerNum(texto) !== (valor ?? null)) setTexto(aTexto(valor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);
  return (
    <Numero valor={texto} unidad={unidad} disabled={disabled} entero={entero} etiqueta={etiqueta}
      onChange={t => { setTexto(t); onChange(leerNum(t)); }} />
  );
}
